import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import { Plus } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import FullOneTimePaymentCard from './FullOneTimePaymentCard.tsx';
import { tourConfigInstallmentsRoute } from '@/features/dashboard/tourConfig/routes/tourConfigInstallmentsRoute.ts';
import InstallmentPaymentCard from '@/features/dashboard/tourConfig/components/InstallmentPaymentCard.tsx';
import {
  BaseInstallmentSchemaType,
  PaymentTiming,
  TourInstallmentForm,
} from '@/features/dashboard/tourConfig/schemas/tourInstallmentsSchema.ts';
import InputError from '@/components/form/base/InputError.tsx';
import { TriangleAlert } from 'lucide-react';
import useGetInstallmentRanges from '@/features/dashboard/tourConfig/hooks/useGetInstallmentRanges.ts';

const defaultInstallment = {
  paymentMethods: [],
  basePricePercentage: undefined,
  dueDaysBeforeStart: undefined,
  dueDaysAfterReservation: undefined,
  paymentTiming: undefined,
};

const TourInstallmentsConfigCard = () => {
  const { tourId } = tourConfigInstallmentsRoute.useParams();

  if (!tourId) {
    throw new Error('Tour ID is required');
  }

  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<TourInstallmentForm>();

  const installments = useWatch({ control, name: 'installments' });
  const allowInstallments = useWatch({ control, name: 'allowInstallments' });

  const { data: installmentRanges } = useGetInstallmentRanges(tourId, {
    installments,
    allowInstallments,
  });

  const { fields, append, replace, remove } = useFieldArray({
    control,
    name: 'installments',
  });

  const totalPercentage = allowInstallments
    ? ((installments as BaseInstallmentSchemaType[]) ?? []).reduce(
        (sum, inst) => sum + (Number(inst.basePricePercentage) || 0),
        0,
      )
    : 0;

  const onAllowInstallmentsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = e.target.checked;
    if (checked) {
      replace([
        {
          paymentMethods: fields[0]?.paymentMethods || [],
          paymentTiming: PaymentTiming.AT_RESERVATION,
          dueDaysBeforeStart: undefined,
          dueDaysAfterReservation: undefined,
          basePricePercentage: undefined,
        },
        defaultInstallment,
      ]);
    } else {
      replace([
        {
          paymentMethods: fields[0]?.paymentMethods || [],
          paymentTiming: undefined,
          dueDaysBeforeStart: undefined,
          dueDaysAfterReservation: undefined,
          basePricePercentage: undefined,
        },
      ]);
    }
  };

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>Płatność za wyjazd</CardTitle>
        <CardDescription>
          Możesz ustawić płatność uczestników jako jednorazową płatność całości
          lub opcjonalnie podzielić płatność na raty.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <CheckboxInput
          label="Zezwól na płatność w ratach"
          register={register('allowInstallments')}
          onChange={onAllowInstallmentsChange}
        />
      </CardContent>

      {allowInstallments ? (
        <>
          <CardContent className="flex flex-col gap-2 space-y-1 rounded-b-[13px] bg-neutral-50 p-2">
            {fields.map((field, index) => {
              return (
                <InstallmentPaymentCard
                  key={field.id}
                  index={index}
                  remove={remove}
                  totalInstallments={fields.length}
                  priceRanges={installmentRanges?.[index] || []}
                />
              );
            })}
            {totalPercentage !== 100 && (
              <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
                <TriangleAlert className="size-3.5 shrink-0" />
                Suma procentów wszystkich rat wynosi {totalPercentage}%
                (wymagane 100%)
              </div>
            )}
            <InputError error={errors.installments?.root?.message} />
          </CardContent>
          <CardContent className="py-2">
            <ActionButton
              tone="outline"
              size="sm"
              onClick={() => append(defaultInstallment)}
            >
              <Plus />
              Dodaj ratę
            </ActionButton>
          </CardContent>
        </>
      ) : (
        <CardContent className="flex flex-col gap-2 space-y-1 bg-neutral-50 p-2">
          <FullOneTimePaymentCard />
        </CardContent>
      )}
    </Card>
  );
};

export default TourInstallmentsConfigCard;
